import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Select, Space, Typography, Tag } from 'antd';
import { BookOutlined, SettingOutlined } from '@ant-design/icons';

import { useAuth } from '../authentication/AuthProvider';
import request from './request';

/**
 * "Which books?" - the accounting screens (chart, posting, reports, VAT,
 * assets, ScanNRead) all work on one set of books: a client's, or the firm's
 * own. The selection is shared across those screens, remembered per company
 * in localStorage, and can be forced by a ?ledger=<id> link (e.g. from the
 * client's page).
 */
const BooksContext = React.createContext(null);

const storageKey = (companyId) => `pms.books.${companyId}`;
const remember = (companyId, ledgerId) => {
  try {
    if (ledgerId) localStorage.setItem(storageKey(companyId), String(ledgerId));
  } catch (e) {
    /* storage unavailable */
  }
};
const recall = (companyId) => {
  try {
    const v = localStorage.getItem(storageKey(companyId));
    return v ? Number(v) : null;
  } catch (e) {
    return null;
  }
};

// A System Administrator's "which company am I looking at" choice, shared by every
// list page and remembered across reloads.
const COMPANY_KEY = 'pms.company';
const rememberCompany = (id) => {
  try {
    if (id) localStorage.setItem(COMPANY_KEY, String(id));
  } catch (e) {
    /* storage unavailable */
  }
};
const recallCompany = () => {
  try {
    const v = localStorage.getItem(COMPANY_KEY);
    return v ? Number(v) : null;
  } catch (e) {
    return null;
  }
};

export function BooksProvider({ children }) {
  const auth = useAuth();
  const [params] = useSearchParams();
  const isSystemAdmin = auth.user?.role === 'SYSTEM_ADMIN';
  const [companies, setCompanies] = React.useState([]);
  const [companyId, setCompanyIdState] = React.useState(auth.user?.companyId || (isSystemAdmin ? recallCompany() : null));
  const setCompanyId = React.useCallback((next) => {
    setCompanyIdState((current) => {
      const value = typeof next === 'function' ? next(current) : next;
      rememberCompany(value);
      return value;
    });
  }, []);
  const [books, setBooks] = React.useState([]);
  const [ledgerId, setLedgerIdState] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const wanted = params.get('ledger') ? Number(params.get('ledger')) : null;

  React.useEffect(() => {
    if (!auth.user) return;
    if (isSystemAdmin) {
      request.get('/companies').then((r) => r.json()).then((r) => {
        if (r.status) {
          setCompanies(r.data);
          // keep the remembered company only if it still exists; otherwise the first one
          setCompanyId((c) => (c && r.data.some((x) => x.id === c) ? c : r.data.length ? r.data[0].id : null));
        }
      });
    } else {
      setCompanyId(auth.user.companyId || null);
    }
  }, [auth.user, isSystemAdmin, setCompanyId]);

  const reload = React.useCallback(() => {
    if (!companyId) return Promise.resolve([]);
    setLoading(true);
    return request
      .get('/ledgers', { company_id: companyId })
      .then((r) => r.json())
      .then((r) => {
        const list = r.status ? r.data : [];
        setBooks(list);
        setLedgerIdState((current) => {
          const candidates = [wanted, current, recall(companyId), list.find((b) => b.client_id)?.id, list[0]?.id];
          const pick = candidates.find((id) => id && list.some((b) => b.id === id)) || null;
          remember(companyId, pick);
          return pick;
        });
        return list;
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  React.useEffect(() => {
    reload();
  }, [reload]);
  // a ?ledger= link switches the books even when the list is already loaded
  React.useEffect(() => {
    if (wanted && books.some((b) => b.id === wanted)) setLedgerIdState(wanted);
  }, [wanted, books]);

  const setLedgerId = (id) => {
    setLedgerIdState(id);
    remember(companyId, id);
  };
  const ledger = books.find((b) => b.id === ledgerId) || null;
  const value = { companies, companyId, setCompanyId, books, ledger, ledgerId, setLedgerId, reload, loading, isSystemAdmin };
  return <BooksContext.Provider value={value}>{children}</BooksContext.Provider>;
}

export function useBooks() {
  const ctx = React.useContext(BooksContext);
  if (!ctx) throw new Error('useBooks must be used inside BooksProvider');
  return ctx;
}

/**
 * The company a list page works on: the user's own company, or - for a System
 * Administrator - the shared, remembered choice from the CompanySelector. Safe
 * outside the provider (client portal logins), where it is simply the login's company.
 */
export function useCompanyId() {
  const auth = useAuth();
  const ctx = React.useContext(BooksContext);
  const local = React.useState(auth.user?.companyId || null);
  if (ctx) return { companyId: ctx.companyId, setCompanyId: ctx.setCompanyId, companies: ctx.companies, isSystemAdmin: ctx.isSystemAdmin };
  return { companyId: local[0], setCompanyId: local[1], companies: [], isSystemAdmin: false };
}

/**
 * Company picker for list pages - renders nothing for a company login. Pass
 * value/onChange/options to drive a page-local choice (e.g. one that also offers
 * the ADMIN template); otherwise it changes the shared choice.
 */
export function CompanySelector({ value, onChange, options, style }) {
  const { companyId, setCompanyId, companies, isSystemAdmin } = useCompanyId();
  if (!isSystemAdmin) return null;
  return (
    <Select
      style={{ width: 200, ...(style || {}) }}
      placeholder="Company"
      value={value === undefined ? companyId : value}
      onChange={onChange || setCompanyId}
      options={options || companies.map((c) => ({ value: c.id, label: c.name }))}
    />
  );
}

export const bookLabel = (b) => (b.client_id ? `${b.Client ? b.Client.client_name : b.name}${b.Client && b.Client.client_code ? ` (${b.Client.client_code})` : ''}` : `${b.name}`);

/** The books picker shown on every accounting screen (plus the company picker for a System Administrator). */
export function BooksSelector({ showManage = true }) {
  const { companies, companyId, setCompanyId, books, ledgerId, setLedgerId, isSystemAdmin, loading } = useBooks();
  const options = [
    ...(books.some((b) => !b.client_id) ? [{ label: 'The firm', options: books.filter((b) => !b.client_id).map((b) => ({ value: b.id, label: bookLabel(b) })) }] : []),
    { label: 'Clients', options: books.filter((b) => b.client_id).map((b) => ({ value: b.id, label: bookLabel(b) })) },
  ];
  return (
    <Space wrap>
      {isSystemAdmin && <Select style={{ width: 170 }} value={companyId} onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />}
      <Select
        showSearch
        optionFilterProp="label"
        loading={loading}
        style={{ minWidth: 260 }}
        value={ledgerId}
        onChange={setLedgerId}
        placeholder="Choose the books"
        options={options}
        suffixIcon={<BookOutlined />}
        notFoundContent={<span>No books yet - <Link to="/books">open books for a client</Link></span>}
      />
      {showManage && (
        <Link to="/books" title="Client books">
          <SettingOutlined />
        </Link>
      )}
    </Space>
  );
}

/** Small caption naming the selected books, for page subtitles. */
export function BooksCaption() {
  const { ledger } = useBooks();
  if (!ledger) return <Typography.Text type="warning">No books selected.</Typography.Text>;
  return (
    <span>
      <Tag color={ledger.client_id ? 'blue' : 'default'}>{ledger.client_id ? 'Client books' : 'Own books'}</Tag>
      <Typography.Text strong>{bookLabel(ledger)}</Typography.Text>
    </span>
  );
}
